import React from "react";
import clsx from "clsx";
import CustomSelect from "../../Shared/CustomSelect/CustomSelect";
import FilterIcon from "../../../img/filter.svg";
import ClosePinkIcon from "../../../img/close-pink.svg";
import {
  geoEntityTabs,
  geoHierarchy,
  geoHierarchyLabelMap,
  getPluralizedLabel,
} from "../utils/kpiDashboardHelper";
import useGeoData from "../../../utilities/useGeoData";

const GeoFilters = ({
  selectedGeoEntity,
  geoFilters,
  onFilterChange,
  resetGeoFilters,
  style,
}) => {
  const hierarchyFilters = Object.entries(geoHierarchy).slice(
    0,
    geoHierarchy[selectedGeoEntity] - 1
  );

  const {
    clusterOptions,
    zoneOptions,
    areaOptions,
    territoryOptions,
    agencyOptions,
  } = useGeoData(selectedGeoEntity, geoFilters);

  const getFilteredOptions = (entity) => {
    switch (entity) {
      case "zone":
        return zoneOptions;
      case "area":
        return areaOptions;
      case "territory":
        return territoryOptions;
      case "agency":
        return agencyOptions;
      default:
        return clusterOptions;
    }
  };

  const computeSummary = (filters) => {
    const activeFilters = Object.entries(geoHierarchy)
      .filter(([entity]) => {
        const value = filters[entity];
        return value && (Array.isArray(value) ? value.length > 0 : true);
      })
      .sort(([, aLevel], [, bLevel]) => bLevel - aLevel);

    if (activeFilters.length === 0) return "";

    const lowestEntity = activeFilters[0][0];
    const lowestValue = filters[lowestEntity];
    const count = Array.isArray(lowestValue) ? lowestValue.length : 1;

    const lowestHierarchyLabel = geoEntityTabs.find(
      (tab) => tab.value === lowestEntity
    )?.label;

    const displayLabel = getPluralizedLabel(lowestHierarchyLabel, count);

    return `${count} ${displayLabel}`;
  };

  const computeActiveFilter = (filters) => {
    const selectedEntity = Object.entries(geoHierarchy)
      .filter(([entity]) => !!filters[entity])
      .sort(([, aLevel], [, bLevel]) => bLevel - aLevel)[0];

    if (!selectedEntity) return null;

    const [entity] = selectedEntity;
    return {
      entity,
      value: filters[entity],
    };
  };

  const handleSelectChange = (geoEntity, selectedValue) => {
    const currentHierarchyLevel = geoHierarchy[geoEntity];

    let newFilters = {
      ...geoFilters,
      [geoEntity]: selectedValue || null,
    };

    Object.entries(geoHierarchy).forEach(([entity, level]) => {
      if (level > currentHierarchyLevel) {
        newFilters[entity] = null;
      }
    });

    onFilterChange(
      newFilters,
      computeSummary(newFilters),
      computeActiveFilter(newFilters)
    );
  };

  return (
    <div className="geo-filter-options" style={style}>
      {hierarchyFilters.length > 0 && (
        <div className="clear-filters-button-wrapper">
          <button className="clear-filters-button" onClick={resetGeoFilters}>
            Clear Filters
            <img src={ClosePinkIcon} alt="clear" />
          </button>
        </div>
      )}
      {hierarchyFilters.map(([geoEntity]) => {
        const hierarchyLabel = geoHierarchyLabelMap[geoEntity] || geoEntity;
        const filteredOptions = getFilteredOptions(geoEntity);

        return (
          <CustomSelect
            key={`${geoEntity}-${filteredOptions.length}-${filteredOptions.map((o) => o.id).join(",")}`}
            isGlassTheme
            isMultiSelect={true}
            item={{
              options: filteredOptions,
              value: geoFilters[geoEntity],
              icon: FilterIcon,
              key: geoEntity,
              label: `Select ${hierarchyLabel}`,
            }}
            placeholder={`Select ${hierarchyLabel}`}
            handleChange={(e) => handleSelectChange(geoEntity, e.target.value)}
            style={{ minWidth: "270px" }}
          />
        );
      })}
    </div>
  );
};

export default GeoFilters;
